import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Plus, X, Star, Ban, ChevronDown, ChevronUp, Check
} from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface FoodPreferencesData {
  allowedCategories: {
    proteins: boolean;
    carbs: boolean;
    vegetables: boolean;
    dairy: boolean;
    fruits: boolean;
  };
  disabledFoods: string[];
  excludedFoods: string[];
  favoriteFoods: string[];
}

// Predefined foods per category
const CATEGORY_FOODS: Record<string, { label: string; foods: string[] }> = {
  proteins: {
    label: 'Proteine',
    foods: [
      'Pui', 'Curcan', 'Vită', 'Porc', 'Miel', 'Pește (somon)', 'Pește (ton)',
      'Pește (cod)', 'Creveți', 'Ouă', 'Tofu', 'Tempeh', 'Fasole neagră',
      'Năut', 'Linte', 'Mazăre', 'Quinoa'
    ]
  },
  carbs: {
    label: 'Carbohidrați',
    foods: [
      'Orez alb', 'Orez brun', 'Paste', 'Pâine albă', 'Pâine integrală',
      'Cartofi', 'Cartofi dulci', 'Ovăz', 'Porumb', 'Hrișcă', 'Mei',
      'Couscous', 'Pâine fără gluten', 'Tortilla'
    ]
  },
  vegetables: {
    label: 'Legume',
    foods: [
      'Broccoli', 'Spanac', 'Morcovi', 'Roșii', 'Castraveți', 'Ardei gras',
      'Dovlecel', 'Vinete', 'Conopidă', 'Varză', 'Salată verde', 'Ceapă',
      'Usturoi', 'Ciuperci', 'Fasole verde', 'Mazăre verde', 'Sfeclă roșie',
      'Țelină', 'Praz', 'Kale'
    ]
  },
  dairy: {
    label: 'Lactate',
    foods: [
      'Lapte de vacă', 'Lapte de migdale', 'Lapte de ovăz', 'Iaurt natural',
      'Iaurt grecesc', 'Brânză de vaci', 'Brânză feta', 'Mozzarella',
      'Parmezan', 'Cheddar', 'Smântână', 'Unt', 'Kefir'
    ]
  },
  fruits: {
    label: 'Fructe',
    foods: [
      'Mere', 'Pere', 'Banane', 'Portocale', 'Mandarine', 'Lămâi',
      'Căpșuni', 'Afine', 'Zmeură', 'Mure', 'Struguri', 'Kiwi',
      'Mango', 'Ananas', 'Pepene verde', 'Pepene galben', 'Piersici',
      'Caise', 'Prune', 'Cireșe'
    ]
  }
};

const CATEGORY_ICONS: Record<string, string> = {
  proteins: '🥩',
  carbs: '🌾',
  vegetables: '🥦',
  dairy: '🥛',
  fruits: '🍎'
};

export default function FoodPreferencesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [prefs, setPrefs] = useState<FoodPreferencesData>({
    allowedCategories: {
      proteins: true,
      carbs: true,
      vegetables: true,
      dairy: true,
      fruits: true
    },
    disabledFoods: [],
    excludedFoods: [],
    favoriteFoods: []
  });

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    proteins: false,
    carbs: false,
    vegetables: false,
    dairy: false,
    fruits: false
  });

  const [newExcluded, setNewExcluded] = useState('');
  const [newFavorite, setNewFavorite] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/api/nutrition/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrefs(response.data);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`${API_URL}/api/nutrition/preferences`, prefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (cat: keyof typeof prefs.allowedCategories) => {
    setPrefs(prev => ({
      ...prev,
      allowedCategories: {
        ...prev.allowedCategories,
        [cat]: !prev.allowedCategories[cat]
      },
      // If disabling category, remove all its foods from disabledFoods
      disabledFoods: !prev.allowedCategories[cat]
        ? prev.disabledFoods.filter(f => !CATEGORY_FOODS[cat].foods.includes(f))
        : prev.disabledFoods
    }));
  };

  const toggleFood = (food: string) => {
    setPrefs(prev => ({
      ...prev,
      disabledFoods: prev.disabledFoods.includes(food)
        ? prev.disabledFoods.filter(f => f !== food)
        : [...prev.disabledFoods, food]
    }));
  };

  const addExcluded = () => {
    const trimmed = newExcluded.trim();
    if (!trimmed || prefs.excludedFoods.includes(trimmed)) return;
    setPrefs(prev => ({ ...prev, excludedFoods: [...prev.excludedFoods, trimmed] }));
    setNewExcluded('');
  };

  const removeExcluded = (food: string) => {
    setPrefs(prev => ({ ...prev, excludedFoods: prev.excludedFoods.filter(f => f !== food) }));
  };

  const addFavorite = () => {
    const trimmed = newFavorite.trim();
    if (!trimmed || prefs.favoriteFoods.includes(trimmed)) return;
    setPrefs(prev => ({ ...prev, favoriteFoods: [...prev.favoriteFoods, trimmed] }));
    setNewFavorite('');
  };

  const removeFavorite = (food: string) => {
    setPrefs(prev => ({ ...prev, favoriteFoods: prev.favoriteFoods.filter(f => f !== food) }));
  };

  const toggleCategoryExpand = (cat: string) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Preferințe Alimentare</h1>
              <p className="text-xs text-gray-500">Personalizează planul tău alimentar</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              saved
                ? 'bg-green-100 text-green-700'
                : 'bg-green-500 text-white hover:bg-green-600 active:scale-95'
            }`}
          >
            {saved ? (
              <><Check className="w-4 h-4" /> Salvat!</>
            ) : saving ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Salvez...</>
            ) : (
              <><Save className="w-4 h-4" /> Salvează</>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <p className="text-sm text-blue-700">
            💡 <strong>Cum funcționează:</strong> Setează preferințele tale alimentare o singură dată, iar planul alimentar va fi generat automat respectând aceste preferințe.
          </p>
        </div>

        {/* SECTION 1: Categories */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-bold text-gray-900 text-base">Categorii de alimente</h2>
            <p className="text-xs text-gray-500 mt-0.5">Activează sau dezactivează categorii întregi</p>
          </div>

          <div className="divide-y divide-gray-50">
            {(Object.keys(CATEGORY_FOODS) as Array<keyof typeof prefs.allowedCategories>).map(cat => {
              const isEnabled = prefs.allowedCategories[cat];
              const catData = CATEGORY_FOODS[cat];
              const disabledCount = catData.foods.filter(f => prefs.disabledFoods.includes(f)).length;
              const isExpanded = expandedCategories[cat];

              return (
                <div key={cat}>
                  <div className="px-5 py-3.5 flex items-center gap-3">
                    <span className="text-2xl">{CATEGORY_ICONS[cat]}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${isEnabled ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                          {catData.label}
                        </span>
                        {disabledCount > 0 && isEnabled && (
                          <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
                            {disabledCount} dezactivate
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{catData.foods.length} alimente</p>
                    </div>

                    {/* Toggle switch */}
                    <button
                      onClick={() => toggleCategory(cat)}
                      className={`relative inline-flex items-center w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                        isEnabled ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        isEnabled ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>

                    {/* Expand button */}
                    {isEnabled && (
                      <button
                        onClick={() => toggleCategoryExpand(cat)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    )}
                  </div>

                  {/* Expanded food list */}
                  {isEnabled && isExpanded && (
                    <div className="px-5 pb-4 bg-gray-50">
                      <p className="text-xs text-gray-500 mb-3">Debifează alimentele pe care nu le vrei:</p>
                      <div className="flex flex-wrap gap-2">
                        {catData.foods.map(food => {
                          const isDisabled = prefs.disabledFoods.includes(food);
                          return (
                            <button
                              key={food}
                              onClick={() => toggleFood(food)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                                isDisabled
                                  ? 'bg-red-50 border-red-200 text-red-500 line-through'
                                  : 'bg-white border-gray-200 text-gray-700 hover:border-green-300 hover:bg-green-50'
                              }`}
                            >
                              {isDisabled ? '✗ ' : '✓ '}{food}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* SECTION 2: Excluded foods */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              <h2 className="font-bold text-gray-900 text-base">Alimente pe care nu le vreau</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Aceste alimente vor fi excluse complet din planul tău</p>
          </div>

          <div className="px-5 py-4">
            {/* Add input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newExcluded}
                onChange={e => setNewExcluded(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addExcluded()}
                placeholder="Ex: ciuperci, nuci, piper..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              />
              <button
                onClick={addExcluded}
                disabled={!newExcluded.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Excluded list */}
            {prefs.excludedFoods.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-3">Niciun aliment exclus</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {prefs.excludedFoods.map(food => (
                  <span
                    key={food}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 rounded-full text-xs font-medium"
                  >
                    {food}
                    <button onClick={() => removeExcluded(food)} className="hover:text-red-800">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SECTION 3: Favorite foods */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <h2 className="font-bold text-gray-900 text-base">Alimente preferate</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Aceste alimente vor fi prioritizate în planul tău</p>
          </div>

          <div className="px-5 py-4">
            {/* Add input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newFavorite}
                onChange={e => setNewFavorite(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addFavorite()}
                placeholder="Ex: somon, avocado, quinoa..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
              />
              <button
                onClick={addFavorite}
                disabled={!newFavorite.trim()}
                className="px-4 py-2 bg-yellow-400 text-white rounded-xl text-sm font-medium hover:bg-yellow-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Favorites list */}
            {prefs.favoriteFoods.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-3">Niciun aliment preferat adăugat</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {prefs.favoriteFoods.map(food => (
                  <span
                    key={food}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-full text-xs font-medium"
                  >
                    ⭐ {food}
                    <button onClick={() => removeFavorite(food)} className="hover:text-yellow-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Save button at bottom */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-4 bg-green-500 text-white rounded-2xl font-bold text-base hover:bg-green-600 active:scale-95 transition-all shadow-lg shadow-green-200 disabled:opacity-60"
        >
          {saving ? 'Se salvează...' : saved ? '✓ Preferințe salvate!' : 'Salvează preferințele'}
        </button>
      </div>
    </div>
  );
}
